import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import {
  Modal,
  Button,
  Input,
  Label,
  MultiSelect,
} from '@atrvd/saas-ui'
import { useCreatePack, useFacilities } from '@/hooks/useBanking'
import { packFormSchema, type PackFormData } from '@/lib/pack-form'

/**
 * Default values used to (re-)initialise the Add form. Module-level const so
 * `reset(...)` after a successful create and the `useForm` defaultValues prop
 * share the same source of truth.
 */
const ADD_PACK_DEFAULTS: PackFormData = {
  name: '',
  facility_ids: [],
}

/**
 * Props for {@link AddPackDialog}.
 *
 * @property open          - Controls dialog visibility.
 * @property onOpenChange  - Called when the dialog should open/close. The
 *                           parent owns the open flag, mirroring the
 *                           EditPackDialog contract for symmetry.
 */
interface AddPackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Modal dialog for creating a new Pack from the Settings → Packs page (DCP-171).
 *
 * Mirrors AddFacilityDialog: react-hook-form + zodResolver(packFormSchema),
 * inline serverError block, toast.success on success, toast.error on failure.
 *
 * Facility MultiSelect only offers facilities with `pack_id === null` — i.e.
 * facilities not already assigned to another pack (AC #4).
 *
 * @param props - See {@link AddPackDialogProps}.
 */
export function AddPackDialog({ open, onOpenChange }: AddPackDialogProps) {
  const createPack = useCreatePack()
  const { data: facilities = [] } = useFacilities()

  /**
   * Last server-side mutation error, surfaced inline above the form so it
   * stays visible after the transient toast fades. Cleared on next submit
   * and on dialog close.
   */
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PackFormData>({
    resolver: zodResolver(packFormSchema),
    defaultValues: ADD_PACK_DEFAULTS,
  })

  /**
   * Reset form to blank defaults each time the dialog opens (closed → open
   * transition). Clears any lingering server error from a previous session.
   */
  useEffect(() => {
    if (open) {
      reset(ADD_PACK_DEFAULTS)
      setServerError(null)
    }
  }, [open, reset])

  /** Close the dialog and reset form state. */
  const handleClose = () => {
    reset(ADD_PACK_DEFAULTS)
    setServerError(null)
    onOpenChange(false)
  }

  /**
   * Handle form submission. On success shows a toast and closes the dialog;
   * on failure surfaces the server message inline and via toast.
   *
   * @param values - Validated form values produced by Zod.
   */
  const onSubmit = async (values: PackFormData) => {
    setServerError(null)
    try {
      await createPack.mutateAsync({
        name: values.name,
        facility_ids: values.facility_ids,
      })
      toast.success('Pack created')
      handleClose()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create pack'
      setServerError(errorMessage)
      toast.error(errorMessage)
    }
  }

  /**
   * Facilities eligible for selection in the Add form: only those not already
   * assigned to a pack. Facilities that belong to another pack are excluded to
   * enforce AC #4 ("a facility cannot be in multiple packs").
   */
  const unassignedOptions = useMemo(
    () =>
      facilities
        .filter((f) => f.pack_id === null)
        .map((f) => ({ value: f.id, label: f.name })),
    [facilities],
  )

  const watchedFacilityIds = watch('facility_ids')

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Add Pack"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={createPack.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-pack-form"
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={createPack.isPending}
          >
            {createPack.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create
          </Button>
        </div>
      }
    >
      <form
        id="add-pack-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 px-1"
      >
        {/*
          Inline server-error block — sibling to the per-field Zod errors
          below. Stays visible until the next submit or dialog close,
          unlike the transient toast.
        */}
        {serverError && (
          <div
            role="alert"
            className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            {serverError}
          </div>
        )}

        {/* Pack Name */}
        <div className="space-y-2">
          <Label htmlFor="add-pack-name">
            Pack Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="add-pack-name"
            placeholder="Enter pack name"
            disabled={createPack.isPending}
            {...register('name')}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Facilities MultiSelect */}
        <div className="space-y-2">
          <Label>
            Facilities <span className="text-destructive">*</span>
          </Label>
          {/*
            MultiSelect is not a native input, so it cannot be wired via
            register(). Instead we read the current value from watch() and
            push updates via setValue(). The `shouldValidate: true` flag
            ensures Zod re-validates the field immediately on change so the
            "Select at least one facility" error clears without waiting for
            the next submit.
          */}
          <MultiSelect
            options={unassignedOptions}
            value={watchedFacilityIds ?? []}
            onChange={(v) => setValue('facility_ids', v, { shouldValidate: true })}
            placeholder="Select facilities"
            searchable
            disabled={createPack.isPending}
          />
          {errors.facility_ids && (
            <p className="text-sm text-destructive">{errors.facility_ids.message}</p>
          )}
        </div>
      </form>
    </Modal>
  )
}
